import { useCallback, useEffect, useRef, useState } from "react";
import RFB from "@novnc/novnc";
import { useAuth } from "../auth/AuthContext";
import { currentAccountId, runApi } from "../lib/runApi";
import type { WorkerSession } from "../lib/runTypes";

function websocketUrl(accountId: string, session: WorkerSession): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const params = new URLSearchParams({
    account_id: accountId,
    token: session.session_token,
  });
  // Closing this socket releases the takeover lease, so the next user can take over.
  if (session.takeover?.lease_id) params.set("takeover", session.takeover.lease_id);
  return `${protocol}//${window.location.host}/api/v1/tasks/${session.task_id}/vnc?${params.toString()}`;
}

export function VncPane({ taskId }: { taskId: string }) {
  const accountId = currentAccountId();
  const { account } = useAuth();
  const myUserId = account?.user?.user_id ?? null;
  const mountRef = useRef<HTMLDivElement | null>(null);
  const rfbRef = useRef<RFB | null>(null);
  const [session, setSession] = useState<WorkerSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const holder = session?.takeover ?? null;
  const iHaveControl = Boolean(holder && myUserId && holder.user_id === myUserId);
  const someoneElseHasControl =
    session?.hitl_state === "human_control" && !iHaveControl;
  const viewOnly = session ? session.hitl_state !== "human_control" : true;

  const load = useCallback(async () => {
    try {
      setSession(await runApi.workerSession(accountId, taskId));
      setError(null);
    } catch {
      setSession(null);
    }
  }, [accountId, taskId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!session || !mountRef.current) return;
    const rfb = new RFB(mountRef.current, websocketUrl(accountId, session), {
      credentials: { password: "" },
    });
    rfb.scaleViewport = true;
    rfb.resizeSession = true;
    rfb.clipViewport = false;
    rfb.viewOnly = session.hitl_state !== "human_control";
    rfb.addEventListener("disconnect", () => {
      rfbRef.current = null;
    });
    rfbRef.current = rfb;
    return () => {
      rfb.disconnect();
      rfbRef.current = null;
    };
  }, [accountId, session]);

  const act = async (action: () => Promise<unknown>, failure: string) => {
    setBusy(true);
    try {
      await action();
      await load();
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : failure);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="tj-card space-y-2" aria-label="VNC supervision">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="serif-heading text-lg">Live session</h2>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span
            className={
              session?.hitl_state === "human_control"
                ? "tj-badge tj-badge-amber"
                : session
                  ? "tj-badge tj-badge-green"
                  : "tj-badge"
            }
          >
            {session
              ? session.hitl_state === "human_control" && holder
                ? `${holder.display_name} has control`
                : session.hitl_state.replace(/_/g, " ")
              : "no live session"}
          </span>
          {session && session.hitl_state !== "human_control" ? (
            <button
              className="tj-btn tj-btn-secondary"
              disabled={busy}
              onClick={() =>
                act(
                  () =>
                    runApi.takeover(
                      accountId,
                      taskId,
                      session.session_token,
                      "takeover from the run console",
                    ),
                  "Takeover failed.",
                )
              }
            >
              Take over
            </button>
          ) : null}
          {session?.hitl_state === "human_control" && iHaveControl ? (
            <button
              className="tj-btn tj-btn-primary"
              disabled={busy}
              onClick={() =>
                act(
                  () => runApi.handback(accountId, taskId, session.session_token),
                  "Handback failed.",
                )
              }
            >
              Hand back
            </button>
          ) : null}
          {someoneElseHasControl ? (
            <button
              className="tj-btn tj-btn-secondary"
              disabled={busy}
              onClick={() =>
                act(
                  () =>
                    runApi.takeover(
                      accountId,
                      taskId,
                      session!.session_token,
                      "request control from the run console",
                    ),
                  "Control request failed.",
                )
              }
            >
              Request control
            </button>
          ) : null}
        </div>
      </div>
      {error ? <p className="tj-badge tj-badge-red">{error}</p> : null}
      {session ? (
        <div
          ref={mountRef}
          className="h-72 w-full overflow-hidden rounded border border-[var(--tj-border)] bg-black"
        />
      ) : (
        <p className="text-xs text-[var(--tm-muted,var(--tj-muted))]">
          No worker session is live for this task. Start one, then take over here for 2FA or
          approval walls.
        </p>
      )}
      {session && viewOnly ? (
        <p className="text-xs text-[var(--tj-muted)]">
          View only. Take over to unlock keyboard and mouse; hand back to capture the session.
        </p>
      ) : null}
    </section>
  );
}
